import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import {
  useDivisionDepartments, useLinkDivisionDepartment, useUnlinkDivisionDepartment,
  useDepartments, useDeleteDepartment, errMessage, type Department,
} from './systemMasters';
import { DepartmentDetailDialog } from './DepartmentDetailDialog';

interface Props { companyId?: string; }

/**
 * Division detail ▸ Departments tab — the many-to-many dbo.CompanyDepartment links.
 * Mirrors the legacy XAF Departments grid + the "Department" Link picker (a checkbox
 * list of every department with search; OK syncs the links). Columns Department / Parent / Sort.
 */
export function DivisionDepartmentsPanel({ companyId }: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const { data: linked = [] } = useDivisionDepartments(companyId);
  const { data: all = [] } = useDepartments();
  const link = useLinkDivisionDepartment(companyId ?? '');
  const unlink = useUnlinkDivisionDepartment(companyId ?? '');
  const del = useDeleteDepartment();
  const qc = useQueryClient();

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [pickOpen, setPickOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [checked, setChecked] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [editDept, setEditDept] = useState<Department | null>(null);   // per-row Edit pencil (LIMS parity)

  const linkedIds = useMemo(() => new Set(linked.map((d) => d.id)), [linked]);

  const openPick = () => { setChecked(new Set(linkedIds)); setPickSearch(''); setPickOpen(true); };
  const pickRows = useMemo(() => {
    const q = pickSearch.trim().toLowerCase();
    return q ? all.filter((d) => (d.name ?? '').toLowerCase().includes(q)) : all;
  }, [all, pickSearch]);

  // OK syncs: link newly-checked, unlink newly-unchecked (legacy DivisionDepartmentController).
  const applyPick = async () => {
    if (!companyId) return;
    setBusy(true);
    try {
      for (const d of all) {
        const isChecked = checked.has(d.id);
        const wasLinked = linkedIds.has(d.id);
        if (isChecked && !wasLinked) await link.mutateAsync(d.id);
        else if (!isChecked && wasLinked) await unlink.mutateAsync(d.id);
      }
      setPickOpen(false);
    } catch (e) { notify.error(errMessage(e)); }
    finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one department to unlink.'); return; }
    if (!await confirm({ title: 'Unlink', message: `Remove ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'} from this division? (The department is kept.)` })) return;
    try { for (const id of selectedIds) await unlink.mutateAsync(String(id)); setSelectedIds(new Set()); }
    catch (e) { notify.error(errMessage(e)); }
  };
  // Delete the department ENTITY (soft-delete) — LIMS Delete on the nested grid; the server's
  // reference guard blocks it if the department still has employees / child departments.
  const doDelete = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one department to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'}? This deletes the department itself.` })) return;
    try { for (const id of selectedIds) await del.mutateAsync(String(id)); setSelectedIds(new Set()); qc.invalidateQueries({ queryKey: ['sm-division-departments'] }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const cmp = (get: (r: Department) => string | null) => (a: Department, b: Department) =>
    (get(a) ?? '').localeCompare(get(b) ?? '', undefined, { sensitivity: 'base' });
  // Equal thirds. LIMS sorts this grid by the primary column only (Company_Departments_ListView →
  // Name SortIndex 0), so ONLY Department carries the sort symbol; Parent / Sort are filter-only.
  const columns: AppColumn<Department>[] = [
    { field: 'name', header: 'Department', width: '34%', minWidth: 160, text: (r) => r.name ?? '', compare: cmp((r) => r.name) },
    { field: 'parent', header: 'Parent', width: '33%', minWidth: 140, sortable: false, text: (r) => r.parent ?? '' },
    { field: 'sort', header: 'Sort', width: '33%', minWidth: 70, align: 'right', type: 'number', sortable: false, text: (r) => (r.sort != null ? String(r.sort) : '') },
  ];
  const pickCols: AppColumn<Department>[] = [
    {
      // Per-row Edit pencil leading (next to the checkbox) — opens the full Department detail.
      field: 'edit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (d) => <button className="pts-icon-btn edit" title="Edit department" onClick={(e) => { e.stopPropagation(); setEditDept(d); }}><i className="ti ti-edit" /></button>,
    },
    { field: 'name', header: 'Department', minWidth: 360, text: (r) => r.name ?? '', compare: cmp((r) => r.name) },
  ];

  if (!companyId) return <div className="acc-tab-pane"><div style={{ padding: 14, color: '#8a7a66' }}>Save the division first to link departments.</div></div>;

  return (
    <div className="acc-subgrid">
      <div className="acc-sub-toolbar">
        <button className="ca-btn" title="Link" onClick={openPick}><i className="ti ti-link" /> Link</button>
        <button className="ca-btn" title="Unlink" onClick={() => void doUnlink()} disabled={selectedIds.size === 0}><i className="ti ti-unlink" /> Unlink</button>
        <button className="ca-btn" title="Edit" disabled><i className="ti ti-edit" /> Edit</button>
        <button className="ca-btn" title="Delete" onClick={() => void doDelete()} disabled={selectedIds.size === 0}><i className="ti ti-trash" /> Delete</button>
        <ExportMenu triggerClassName="ca-btn" filename="Division-Departments" headers={['Department', 'Parent', 'Sort']}
          rows={linked.map((r) => [r.name ?? '', r.parent ?? '', r.sort != null ? String(r.sort) : ''])} />
      </div>
      <AppDataGrid<Department> rows={linked} columns={columns} getRowId={(r) => r.id}
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
        emptyText="No departments linked."
        defaultPageSize={20} />

      {pickOpen && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); setPickOpen(false); }}>
          <div className="acc-dialog acc-dialog-pick" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>Department</span><i className="ti ti-x acc-dialog-x" onClick={() => setPickOpen(false)} /></div>
            <div className="acc-dialog-body">
              <div className="acc-pick-search"><div className="acc-search"><i className="ti ti-search" />
                <input autoFocus placeholder="Search…" value={pickSearch} onChange={(e) => setPickSearch(e.target.value)} />
                {pickSearch && <i className="ti ti-x acc-search-x" onClick={() => setPickSearch('')} />}
              </div></div>
              <AppDataGrid<Department> rows={pickRows} columns={pickCols} getRowId={(r) => r.id}
                selectable selectedIds={checked} onSelectionChange={setChecked}
                initialSort={{ key: 'name', dir: 'asc' }} emptyText="No departments found."
                defaultPageSize={20} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void applyPick()} disabled={busy}>OK</button>
              <button className="ca-btn" onClick={() => setPickOpen(false)} disabled={busy}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Per-row Edit pencil → full Department detail (LIMS parity). */}
      {editDept && <DepartmentDetailDialog department={editDept} onClose={() => setEditDept(null)} />}
    </div>
  );
}
